import { t } from 'i18next';

import { OFFER_TYPES } from '~constants/buildPizza';
import { productTypes } from '~constants/products';
import { compareArrays, mergeArrayItems, addAtIndex } from '~utils/array';
import { compareObjectsByKeys } from '~utils/object';

export const isComplement = item =>
  [productTypes.COMPLEMENT, productTypes.COMPLEMENT_VARIANT].includes(item?.menuableType);

export const isProduct = item => Object.values(productTypes).includes(item?.menuableType);

export const isOffer = item => Object.values(OFFER_TYPES).includes(item?.offerType);

export const isPizzaByHalves = item => !!item?.halves;

export const isByOp = item => item?.name === t('PizzaCreation:makePizza');

export const isCustomPizza = (item, byopVariants) =>
  byopVariants?.some(
    variant => variant?.menuableId === item?.menuableId && variant?.menuableType === item?.menuableType
  );

export const isProductVariant = item =>
  [productTypes.COMPLEMENT_VARIANT, productTypes.PIZZA_VARIANT].includes(item?.menuableType);

export const getMenuItem = (item, flattenedMenu) => {
  const id = `${item?.menuableType}${item?.menuableId}`;
  return flattenedMenu?.[id];
};

export const getProductId = (item, flattenedMenu) => {
  const menuItem = getMenuItem(item, flattenedMenu);
  return menuItem?.data?.menuable?.[isProductVariant(menuItem) ? 'productId' : 'id'];
};

export const getMenuItemData = (item, flattenedMenu, sizesAndCrusts) => {
  const menuItem = getMenuItem(item, flattenedMenu);
  const menuableData = menuItem?.data?.menuable;
  const hasSideCategory = !!item?.side;

  return {
    name:
      isComplement(item) && item?.menuableType === productTypes.COMPLEMENT
        ? menuableData?.name
        : menuableData?.productName,
    variantName: isComplement(item)
      ? hasSideCategory
        ? item?.side?.name
        : menuableData?.name
      : [menuableData?.size, menuableData?.crust].map(variant => sizesAndCrusts.crusts?.[variant]).join(', ')
  };
};

export const compareIngredients = (ingredient1, ingredient2) =>
  compareObjectsByKeys(ingredient1, ingredient2, ['id', 'action', 'quantity']);

export const pizzasEquals = (pizza1, pizza2) =>
  !(pizza1.offerId || pizza2.offerId) &&
  !(pizza1.halves || pizza2.halves) &&
  compareObjectsByKeys(pizza1, pizza2, ['menuableId', 'menuableType']) &&
  compareArrays(pizza1?.ingredients, pizza2?.ingredients, compareIngredients);

export const mergePizzas = (pizza, pizzaFound) => ({
  ...pizza,
  ...pizzaFound,
  ...['quantity', 'discount', 'subtotal', 'total'].reduce(
    (acc, key) => ({ ...acc, [key]: pizza?.[key] + pizzaFound?.[key] }),
    {}
  )
});

export const mergePizzaItems = items => mergeArrayItems(items, pizzasEquals, mergePizzas);

export const getProductUID = product =>
  isOffer(product)
    ? `${product.offerType}${product.offerId}`
    : `${product.menuableType}${product.menuableId}`;

const productMapper = (item, storeItem) =>
  isOffer(item)
    ? {
        id: item.id,
        offerId: item.offerId,
        offerType: item.offerType,
        quantity: item.quantity,
        name: item.name,
        groupProducts: item.groupProducts,
        price: item.price
      }
    : {
        ...storeItem.data,
        quantity: item.quantity,
        price: item.price,
        name: item.halves ? storeItem.menuable?.productName : item.name,
        variant: item.halves ? item.name : item.variantName || item.variant,
        ...(item.side && { side: item.side }),
        ...(item.ingredients && { ingredients: item.ingredients }),
        ...(item.halves && { halves: item.halves })
      };

const productEquals = (productA, productB) =>
  productA?.menuableId === productB?.menuableId && productA?.menuableType === productB?.menuableType;

export const getMissingCartItems = (items, storeItems) =>
  items?.reduce(
    (acc, item) => {
      const UID = getProductUID(item);
      if (storeItems[UID]) {
        acc.found.push(productMapper(item, storeItems[UID]));
      } else {
        acc.missing.push(item);
      }
      return acc;
    },
    { found: [], missing: [] }
  );

export const mergeShoppingCartItems = (shoppingCartItems, foundItems) =>
  [...shoppingCartItems, ...foundItems].reduce((acc, item) => {
    const itemFoundIndex = acc.findIndex(accItem => productEquals(accItem, item));
    return itemFoundIndex < 0
      ? [...acc, { ...item.menuable, ...item }]
      : addAtIndex(acc, itemFoundIndex, {
          ...acc[itemFoundIndex],
          quantity: item?.quantity + acc[itemFoundIndex]?.quantity
        });
  }, []);
